import { Link, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useState, useRef } from "react";
import Select from "react-select";
import { useSelector, useDispatch } from "react-redux";
import swal from "sweetalert";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";
import { SortOrderDropDownSelection } from "../../components/CustomComponents/DropdownSelections/DropDownSelections";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import {
  setSortBy,
  setSortOrder,
  setVisibleColumns,
} from "../../app/itemGridPreferencesSlice";
import {
  mergeVisibleColumns,
  needsPreferencesUpdate,
} from "../../utils/preferencesUtils";
import { MANAGE_PAGE_SEARCH_DEBOUNCE_MS } from "../../utils/managePageConstants";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import Pagination from "../../components/CustomComponents/Pagination/pagination";

const ManageSuppliers = () => {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const [search, setSearch] = useState("");
  const pageKey = "suppliers";
  const defaultVisibleColumns = {
    email: true,
    contact: true,
    city: true,
    postcode: true,
  };

  const defaultSortOrder = { value: "ASC", label: "ASC" };
  const defaultSortBy = { value: "S_ID", label: "ID" };
  const preferences = useSelector(
    (state) => state.itemGridPreferences[pageKey] || {}
  );
  const visibleColumns = mergeVisibleColumns(
    preferences.visibleColumns,
    defaultVisibleColumns
  );
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const dispatch = useDispatch();
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  const themeColor = useThemeColor();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [pageSize, setPageSize] = useState({ value: "50", label: "50" });
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const lastSelectedIndex = useRef(null);
  const navigate = useNavigate();

  const sortByOptions = [
    { value: "S_ID", label: "ID" },
    { value: "S_Name", label: "Name" },
  ];

  const clearFilters = () => {
    setSearch("");
    setPageNumber(1);
  };

  const DeleteSupplier = async (supplier) => {
    const confirmDelete = await swal({
      title: "Are you sure?",
      text: `Are you sure you want to delete ${supplier.sName}?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (!confirmDelete) {
      return;
    }

    setLoading(true);
    try {
      await axiosInstance
        .delete(APIs.DELETE_SUPPLIER_BY_ID, {
          params: {
            CompID,
            S_ID: supplier.s_ID,
          },
        })
        .then((response) => {
          if (response.data.status) {
            setSuppliers((prevGroups) =>
              prevGroups.filter((delGroup) => delGroup.s_ID !== supplier.s_ID)
            );
            showSuccessToast("Supplier is Deleted.");
          } else showErrorToast(response.data.message);
        });
      setLoading(false);
    } catch (err) {
      showErrorToast("Something went wrong.");
      setLoading(false);
    }
  };

  const handleSelectItem = (supplierId, index, event) => {
    setSelectedItems((prev) => {
      const newSelected = new Set(prev);

      if (event.shiftKey && lastSelectedIndex.current !== null) {
        const start = Math.min(lastSelectedIndex.current, index);
        const end = Math.max(lastSelectedIndex.current, index);
        for (let i = start; i <= end; i++) {
          if (suppliers[i]?.s_ID) {
            newSelected.add(suppliers[i].s_ID);
          }
        }
      } else {
        if (newSelected.has(supplierId)) {
          newSelected.delete(supplierId);
        } else {
          newSelected.add(supplierId);
        }
        lastSelectedIndex.current = index;
      }

      return newSelected;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Set(suppliers.map((item) => item.s_ID));
      setSelectedItems(newSelected);
      lastSelectedIndex.current = null;
    } else {
      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
    }
  };

  const handleDeselectAll = () => {
    setSelectedItems(new Set());
    lastSelectedIndex.current = null;
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) {
      showErrorToast("No suppliers selected for deletion.");
      return;
    }

    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${selectedItems.size} selected suppliers?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      await Promise.all(
        Array.from(selectedItems).map((supplierId) =>
          axiosInstance.delete(APIs.DELETE_SUPPLIER_BY_ID, {
            params: {
              CompID,
              S_ID: supplierId,
            },
          })
        )
      );

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      fetchSuppliers();
      showSuccessToast(`${selectedItems.size} suppliers deleted successfully.`);
      setLoading(false);
    } catch (err) {
      showErrorToast("Failed to delete selected suppliers.");
      setLoading(false);
    }
  };

  const handleBulkEdit = () => {
    if (selectedItems.size === 0) {
      showErrorToast("No suppliers selected for editing.");
      return;
    }

    const selectedSupplierIds = Array.from(selectedItems);
    navigate(`/supplier/bulkEdit`, { state: { selectedSupplierIds } });
  };

  const fetchSuppliers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_SUPPLIERS, {
        params: {
          CompID,
          Search: search?.trim() || "",
          SortBy: sortBy.value,
          SortOrder: sortOrder.value,
          PageNumber: pageNumber,
          PageSize: pageSize.value,
        },
      });

      const suppliersData = response.data.data || [];

      setTotalPages(response.data.pageCount || 0);
      setTotalRecords(response.data.totalRecords || 0);

      if (Array.isArray(suppliersData)) {
        setSuppliers(suppliersData);
      } else {
        setSuppliers([]);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setSuppliers([]);
      showErrorToast("Failed to fetch suppliers.");
    }
  }, [CompID, search, sortBy, sortOrder, pageNumber, pageSize]);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchSuppliers();
    }, MANAGE_PAGE_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [fetchSuppliers]);

  useEffect(() => {
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else {
      if (
        needsPreferencesUpdate(
          preferences.visibleColumns,
          defaultVisibleColumns
        )
      ) {
        const mergedColumns = mergeVisibleColumns(
          preferences.visibleColumns,
          defaultVisibleColumns
        );
        dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
      }
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    // eslint-disable-next-line
  }, [dispatch, pageKey]);

  const GoToEdit = (s_ID) => navigate(`/supplier/updateSupplier/${s_ID}`);
  console.log("Rendering ManageSuppliers with suppliers:", suppliers);

  return (
    <div>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Manage Supplier</li>
          </ol>
          <h1 className="page-header mb-0">Manage Supplier</h1>
        </div>

        <div className="ms-auto">
          <Link to="/supplier/AddSupplier">
            <button
              className="btn btn-rounded px-4 rounded-pill"
              style={{
                backgroundColor: themeColor,
                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                border: "none",
                color: "white",
                transition: "all 0.3s ease",
                boxShadow: `0 2px 8px ${themeColor}40`,
                fontSize: "14px",
                fontWeight: "600",
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = "translateY(-2px)";
                e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
              }}
            >
              <i
                className="fa fa-plus fa-lg me-2 ms-n2"
                style={{ color: "white" }}
              ></i>
              Add Supplier
            </button>
          </Link>
        </div>
      </div>
      <div className="mb-3 d-sm-flex fw-bold">
        <div className="mt-sm-0 mt-2">
          <a href="#/" className="text-dark text-decoration-none">
            <i className="fa fa-download fa-fw me-1 text-dark text-opacity-50"></i>{" "}
            Export
          </a>
        </div>
        <div className="ms-sm-4 ps-sm-1 mt-sm-0 mt-2">
          <a href="#/" className="text-dark text-decoration-none">
            <i className="fa fa-upload fa-fw me-1 text-dark text-opacity-50"></i>{" "}
            Import
          </a>
        </div>
        <div className="ms-sm-4 ps-sm-1 mt-sm-0 mt-2 dropdown-toggle">
          <a
            href="#/"
            data-bs-toggle="dropdown"
            className="text-dark text-decoration-none"
          >
            <i className="fa fa-cog fa-fw me-1 text-dark text-opacity-50"></i>{" "}
            More Actions
          </a>
          <div className="dropdown-menu">
            <a className="dropdown-item" href="#/">
              Action
            </a>
            <a className="dropdown-item" href="#/">
              Another action
            </a>
            <a className="dropdown-item" href="#/">
              Something else here
            </a>
            <div role="separator" className="dropdown-divider"></div>
            <a className="dropdown-item" href="#/">
              Separated link
            </a>
          </div>
        </div>
      </div>
      <div className="mb-3 mt-2">
        {(suppliers.length > 0 || search.trim() || !loading) && (
          <div className="card border-0">
            <div className="tab-content p-3">
              <div className="tab-pane fade show active" id="allTab">
                {/* Enhanced Filter Section */}
                <div className="mb-4">
                  <div
                    className="card filter-card"
                    style={{
                      position: "relative",
                      zIndex: 1,
                      background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                      borderRadius: '12px',
                      border: 'none',
                      boxShadow: `0 4px 20px ${themeColor}40`
                    }}
                  >
                    <div className="card-body p-3">
                      {/* Filter Controls */}
                      <div className="row g-3 align-items-center">
                        {/* Search Field */}
                        <div className="col-xl-4 col-lg-4 col-md-6">
                          <div className="search-input filter-control">
                            <input
                              type="text"
                              className="filter-input"
                              value={search}
                              onChange={(e) => {
                                setSearch(e.target.value);
                                setPageNumber(1);
                              }}
                              placeholder="Search by name, email, contact..."
                              style={{
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                color: '#333'
                              }}
                            />
                          </div>
                        </div>

                        {/* Sort By Dropdown */}
                        <div className="col-xl-3 col-lg-3 col-md-6">
                          <div className="filter-control">
                            <Select
                              value={sortBy}
                              onChange={(val) => {
                                dispatch(setSortBy({ pageKey, sortBy: val }));
                                setPageNumber(1);
                              }}
                              options={sortByOptions}
                              styles={dropdownStyles}
                              placeholder="Sort By"
                              className="filter-dropdown"
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              menuShouldBlockScroll={false}
                              menuShouldScrollIntoView={false}
                              closeMenuOnScroll={false}
                            />
                          </div>
                        </div>

                        {/* Sort Order Dropdown */}
                        <div className="col-xl-2 col-lg-2 col-md-6">
                          <div className="filter-control">
                            <SortOrderDropDownSelection
                              defaultValue={sortOrder}
                              onChange={(val) => {
                                dispatch(setSortOrder({ pageKey, sortOrder: val }));
                                setPageNumber(1);
                              }}
                            />
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="col-xl-3 col-lg-3 col-md-12">
                          <div className="d-flex gap-2">
                            <button
                              className="clear-btn"
                              onClick={clearFilters}
                              title="Clear all filters"
                            >
                              <i className="fa fa-times me-1"></i>
                              Filter
                            </button>

                            <ColumnSelection
                              visibleColumns={visibleColumns}
                              setVisibleColumns={(cols) =>
                                dispatch(setVisibleColumns({ pageKey, columns: cols }))
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Selection Info and Actions (Shown only when suppliers are selected) */}
                {selectedItems.size > 0 && (
                  <div className="mb-3">
                    <div
                      className="p-3 bg-light rounded d-flex align-items-center justify-content-between"
                      style={{ border: "1px solid #dee2e6" }}
                    >
                      <div className="d-flex align-items-center gap-3">
                        <span className="fw-bold text-primary">
                          {selectedItems.size} supplier
                          {selectedItems.size > 1 ? "s" : ""} selected
                        </span>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={handleDeselectAll}
                        >
                          Clear Selection
                        </button>
                      </div>

                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={handleBulkEdit}
                          title="Edit Selected Suppliers"
                        >
                          <i className="fa fa-edit me-1"></i>
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={handleBulkDelete}
                          title="Delete Selected Suppliers"
                        >
                          <i className="fa fa-trash me-1"></i>
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Empty State Message */}
                {suppliers.length === 0 && !loading && (
                  <div className="text-center py-5">
                    <div className="mb-4">
                      <i className="fa fa-building fa-3x text-muted opacity-50"></i>
                    </div>
                    <h5 className="text-muted mb-3">No Suppliers Found</h5>
                    <p className="text-muted mb-4">
                      {search.trim()
                        ? `No suppliers match your search criteria "${search.trim()}"`
                        : "You haven't added any suppliers yet."}
                    </p>
                    {!search.trim() && (
                      <Link to="/supplier/AddSupplier">
                        <button
                          className="btn px-4"
                          style={{
                            backgroundColor: themeColor,
                            background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                            border: "none",
                            color: "white",
                            borderRadius: "8px",
                            padding: "12px 24px",
                            fontSize: "14px",
                            fontWeight: "600",
                            transition: "all 0.3s ease",
                            boxShadow: `0 2px 8px ${themeColor}40`,
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                            e.target.style.transform = "translateY(-1px)";
                            e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                            e.target.style.transform = "translateY(0)";
                            e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
                          }}
                        >
                          <i className="fa fa-plus me-2"></i>
                          Add Your First Supplier
                        </button>
                      </Link>
                    )}
                    {search.trim() && (
                      <button
                        className="btn btn-outline-secondary"
                        onClick={clearFilters}
                      >
                        <i className="fa fa-times me-2"></i>
                        Clear Search
                      </button>
                    )}
                  </div>
                )}

                {suppliers.length > 0 && (
                  <div
                    className="table-responsive mt-3"
                    style={{
                      minHeight: "200px",
                      maxHeight: "450px",
                      overflowY: "auto",
                      overflowX: "auto",
                    }}
                  >
                    <table className="table table-hover">
                      <thead
                        className="bg-light"
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 10,
                          backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                          color: isDarkMode ? "#ffffff" : "#000000",
                        }}
                      >
                        <tr>
                          <th className="pt-2 pb-2">
                            <input
                              type="checkbox"
                              onChange={handleSelectAll}
                              checked={
                                suppliers.length > 0 &&
                                selectedItems.size === suppliers.length
                              }
                            />
                          </th>
                          <th className="pt-2 pb-2">Supplier Name</th>
                          {visibleColumns.email && (
                            <th className="pt-2 pb-2 text-center">Email</th>
                          )}
                          {visibleColumns.contact && (
                            <th className="pt-2 pb-2 text-center">Contact</th>
                          )}
                          {visibleColumns.city && (
                            <th className="pt-2 pb-2 text-center">City</th>
                          )}
                          {visibleColumns.postcode && (
                            <th className="pt-2 pb-2 text-center">Post Code</th>
                          )}
                          <th className="pt-2 pb-2 text-center">Actions</th>
                        </tr>
                      </thead>

                      <tbody>
                        {suppliers.map((supplier, index) => (
                          <tr key={supplier.s_ID}>
                            <td className="align-middle">
                              <input
                                type="checkbox"
                                checked={selectedItems.has(supplier.s_ID)}
                                onChange={(e) =>
                                  handleSelectItem(supplier.s_ID, index, e)
                                }
                                data-chkbox-shiftsel="type1"
                              />
                            </td>
                            <td
                              className="align-middle"
                              onClick={() => {
                                GoToEdit(supplier.s_ID);
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              {supplier.s_Name}
                            </td>
                            {visibleColumns.email && (
                              <td
                                className="align-middle text-center"
                                onClick={() => {
                                  GoToEdit(supplier.s_ID);
                                }}
                                style={{ cursor: "pointer" }}
                              >
                                {supplier.s_Email}
                              </td>
                            )}
                            {visibleColumns.contact && (
                              <td
                                className="align-middle text-center"
                                onClick={() => {
                                  GoToEdit(supplier.s_ID);
                                }}
                                style={{ cursor: "pointer" }}
                              >
                                {supplier.s_Contact}
                              </td>
                            )}
                            {visibleColumns.city && (
                              <td
                                className="align-middle text-center"
                                onClick={() => {
                                  GoToEdit(supplier.s_ID);
                                }}
                                style={{ cursor: "pointer" }}
                              >
                                {supplier.s_City}
                              </td>
                            )}
                            {visibleColumns.postcode && (
                              <td
                                className="align-middle text-center"
                                onClick={() => {
                                  GoToEdit(supplier.s_ID);
                                }}
                                style={{ cursor: "pointer" }}
                              >
                                {supplier.post_Code}
                              </td>
                            )}
                            <td className="align-middle text-center">
                              <div className="dropdown">
                                <button
                                  className="btn btn-sm no-caret"
                                  type="button"
                                  data-bs-toggle="dropdown"
                                >
                                  <i className="fa fa-ellipsis-h text-muted"></i>
                                </button>
                                <div
                                  className="dropdown-menu"
                                  style={{ zIndex: 1050 }}
                                >
                                  <Link
                                    to={`/supplier/updateSupplier/${supplier.s_ID}`}
                                    className="dropdown-item"
                                  >
                                    Edit
                                  </Link>
                                  <button
                                    className="dropdown-item text-danger"
                                    onClick={() => DeleteSupplier(supplier)}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <Pagination
                  totalRecords={totalRecords}
                  pageSize={pageSize}
                  pageNumber={pageNumber}
                  totalPages={totalPages}
                  setPageNumber={setPageNumber}
                  setPageSize={setPageSize}
                  themeColor={themeColor}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageSuppliers;
